"use client"

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useTheme } from './ThemeProvider';
import { createPortal } from 'react-dom';

interface HeaderProps {
  isScrolled: boolean;
  scrollProgress: number;
}

const Header = ({ isScrolled, scrollProgress }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showClientAreaTooltip, setShowClientAreaTooltip] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { theme, toggleTheme } = useTheme();
  const [isBrowser, setIsBrowser] = useState(false);

  // Detectar se estamos no navegador para usar portal
  useEffect(() => {
    setIsBrowser(true);
  }, []);

  // Clear tooltip timer when component unmounts
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Desativar rolagem quando menu mobile estiver aberto
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleClientAreaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowClientAreaTooltip(true);
    
    // Hide the tooltip after 3 seconds
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    
    tooltipTimerRef.current = setTimeout(() => {
      setShowClientAreaTooltip(false);
    }, 3000);
  };

  // Mobile menu with Framer Motion animations
  const MobileMenu = () => {
    if (!isMobileMenuOpen) return null;
    
    // Animation variants
    const fadeIn = {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.6 } }
    };
    
    const slideUp = {
      hidden: { y: 20, opacity: 0 },
      visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
    };
    
    const staggerContainer = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.08,
          delayChildren: 0.1
        }
      }
    };
    
    return (
      <>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        <motion.div 
          initial={{ x: '100%', opacity: 0.5 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={`fixed inset-y-0 right-0 w-[300px] ${
            theme === 'dark' 
              ? 'bg-gray-900/80' 
              : 'bg-blue-900/70'
          } backdrop-blur-md z-[9999] flex flex-col overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-end p-4 border-b border-white/10">
            <motion.button 
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>
          
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="flex-1 py-4 overflow-y-auto"
          >
            <nav className="flex flex-col space-y-6 px-6 text-center">
              <motion.a 
                variants={slideUp}
                href="#hero" 
                className="text-white text-xl font-light hover:text-blue-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </motion.a>
              <motion.a 
                variants={slideUp}
                href="#servicos" 
                className="text-white/80 text-xl font-light hover:text-blue-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Serviços
              </motion.a>
              <Link
                href="/portfolio"
                className="text-white/80 text-xl font-light hover:text-blue-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Portfólio
              </Link>
              <Link
                href="/sobre" 
                className="text-white/80 text-xl font-light hover:text-blue-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sobre
              </Link>
              <motion.a 
                variants={slideUp}
                href="#contato" 
                className="text-white/80 text-xl font-light hover:text-blue-200 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contato
              </motion.a>
              
              {/* Links de contato */}
              <div className="mt-6 flex flex-col space-y-4">
                <motion.a 
                  variants={slideUp}
                  href="tel:+551932060706"
                  className="flex items-center justify-center text-white/80 hover:text-blue-200 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  (19) 3206-0706
                </motion.a>
                
                <motion.a 
                  variants={slideUp}
                  href="https://wa.me/5519994799400"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center text-white/80 hover:text-green-300 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  (19) 99479-9400
                </motion.a>
                
                {/* Área do Cliente Mobile com tooltip */}
                <motion.div 
                  variants={slideUp}
                  className="relative inline-block mx-auto mt-4"
                >
                  <motion.a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleClientAreaClick(e);
                    }}
                    className="inline-flex items-center justify-center px-6 py-2 rounded-xl text-white bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 transition-all"
                  >
                    Área do Cliente
                  </motion.a>
                  
                  {/* Tooltip para mobile */}
                  <AnimatePresence>
                    {showClientAreaTooltip && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-2 rounded-lg text-xs font-semibold shadow-lg z-50 bg-blue-800 text-white"
                      >
                        EM BREVE
                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-inherit"></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </nav>
          </motion.div>
          
          <motion.div 
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            className="border-t border-white/10 p-6 mt-auto"
          >
            <div className="flex justify-center space-x-6">
              <motion.a 
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                href="#" 
                className="text-white/60 hover:text-white"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.2, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
                href="#" 
                className="text-white/60 hover:text-white"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                href="https://wa.me/5519994799400" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-green-400"
                aria-label="WhatsApp"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </>
    );
  };

  return (
    <>
      {/* Scroll progress indicator */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-blue-600 z-50 origin-left"
        style={{ scaleX: scrollProgress }}
      />
      
      {/* Header fixo com z-index alto */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed w-full z-40 transition-all duration-300 ${
          isScrolled 
            ? theme === 'dark' 
              ? 'bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-800' 
              : 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container max-w-7xl mx-auto px-4">
          <div className={`flex justify-between items-center transition-all duration-500 ease-in-out ${
            isScrolled ? 'h-20 md:h-24 lg:h-28' : 'h-28 md:h-32 lg:h-36'
          }`}>
            {/* Logo com dimensões maiores mas ainda centralizado verticalmente */}
            <Link href="/" className="flex-shrink-0 z-10">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center h-full py-6" // Padding vertical aumentado significativamente
              >
                <div 
                  className={`
                    relative rounded-lg overflow-hidden 
                  `}
                >
                  {/* Dimensões dinâmicas baseadas no scroll - menores em mobile */}
                  <div className={`
                    relative transition-all duration-500 ease-in-out
                    ${isScrolled 
                      ? 'w-32 h-16 sm:w-40 sm:h-20 md:w-48 md:h-24 lg:w-56 lg:h-28' 
                      : 'w-48 h-24 sm:w-56 sm:h-28 md:w-72 md:h-36 lg:w-80 lg:h-36'
                    }
                  `}>
                    <Image 
                      src="/images/logo.png" 
                      alt="Web Imports - Eletrônicos & Tecnologia" 
                      fill
                      sizes={isScrolled 
                        ? "(max-width: 640px) 8rem, (max-width: 768px) 10rem, (max-width: 1024px) 12rem, 14rem"
                        : "(max-width: 640px) 12rem, (max-width: 768px) 14rem, (max-width: 1024px) 18rem, 20rem"
                      }
                      priority
                      className={`object-contain transition-all duration-500 ${
                        !isScrolled ? 'filter drop-shadow-[0_0_20px_rgba(255,255,255,0.7)]' : ''
                      }`}
                    />
                  </div>
                </div>
              </motion.div>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              <motion.a 
                href="#hero"
                whileHover={{ scale: 1.05 }}
                className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isScrolled 
                    ? theme === 'dark' 
                      ? 'text-blue-400' 
                      : 'text-blue-900'
                    : 'text-white'
                }`}
                aria-label="Ir para home"
              >
                Home
                <motion.span
                  layoutId="navIndicator"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-current rounded-full"
                />
              </motion.a>
              <motion.a 
                href="#servicos"
                whileHover={{ scale: 1.05 }}
                className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isScrolled 
                    ? theme === 'dark' 
                      ? 'text-gray-300 hover:text-blue-400' 
                      : 'text-gray-700 hover:text-blue-900'
                    : 'text-white/80 hover:text-white'
                }`}
                aria-label="Ir para serviços"
              >
                Serviços
              </motion.a>
              <Link 
                href="/portfolio"
                className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isScrolled 
                    ? theme === 'dark' 
                      ? 'text-gray-300 hover:text-blue-400' 
                      : 'text-gray-700 hover:text-blue-900'
                    : 'text-white/80 hover:text-white'
                }`}
                aria-label="Ir para portfólio"
              >
                Portfólio
              </Link>
              <Link 
                href="/sobre"
                className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isScrolled 
                    ? theme === 'dark' 
                      ? 'text-gray-300 hover:text-blue-400' 
                      : 'text-gray-700 hover:text-blue-900'
                    : 'text-white/80 hover:text-white'
                }`}
                aria-label="Ir para sobre"
              >
                Sobre
              </Link>
              <motion.a 
                href="#contato"
                whileHover={{ scale: 1.05 }}
                className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isScrolled 
                    ? theme === 'dark' 
                      ? 'text-gray-300 hover:text-blue-400' 
                      : 'text-gray-700 hover:text-blue-900'
                    : 'text-white/80 hover:text-white'
                }`}
                aria-label="Ir para contato"
              >
                Contato
              </motion.a>
              
              <div className="ml-6 flex items-center space-x-3">
                {/* Telefone */}
                <motion.a 
                  href="tel:+551932060706"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center text-sm font-medium ${
                    isScrolled 
                      ? theme === 'dark' 
                        ? 'text-gray-300 hover:text-blue-400' 
                        : 'text-gray-700 hover:text-blue-900'
                      : 'text-white/90 hover:text-white'
                  }`}
                  aria-label="Ligar para (19) 3206-0706"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="hidden sm:inline">(19) 3206-0706</span>
                </motion.a>

                {/* Separador */}
                <div className="h-4 w-px bg-gray-400/30"></div>

                {/* WhatsApp */}
                <motion.a 
                  href="https://wa.me/5519994799400"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center text-sm font-medium ${
                    isScrolled 
                      ? theme === 'dark' 
                        ? 'text-gray-300 hover:text-green-400' 
                        : 'text-gray-700 hover:text-green-600'
                      : 'text-white/90 hover:text-green-300'
                  }`}
                  aria-label="Enviar mensagem no WhatsApp (19) 99479-9400"
                >
                  <svg className="w-5 h-5 mr-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="hidden sm:inline">(19) 99479-9400</span>
                </motion.a>

                {/* Separador */}
                <div className="h-4 w-px bg-gray-400/30"></div>

                {/* Área do Cliente com tooltip EM BREVE */}
                <div className="relative">
                  <motion.a 
                    href="#"
                    onClick={handleClientAreaClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm ${
                      isScrolled 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-md hover:shadow-lg' 
                        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20'
                    } transition-all`}
                    aria-label="Acessar área do cliente"
                  >
                    Área do Cliente
                  </motion.a>
                  
                  {/* Tooltip de EM BREVE */}
                  <AnimatePresence>
                    {showClientAreaTooltip && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className={`absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-2 rounded-lg text-xs font-semibold shadow-lg z-50 ${
                          theme === 'dark' 
                            ? 'bg-blue-900 text-white' 
                            : 'bg-blue-700 text-white'
                        }`}
                      >
                        EM BREVE
                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-inherit"></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Theme toggle button (Dark Mode) */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleTheme}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    isScrolled
                      ? theme === 'dark'
                        ? 'bg-gray-800 text-gray-200'
                        : 'bg-gray-100 text-gray-700'
                      : 'bg-white/10 text-white'
                  }`}
                  aria-label={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
                >
                  {theme === 'dark' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </motion.button>
              </div>
            </nav>
            
            {/* Mobile menu button */}
            <div className="lg:hidden z-20 flex items-center space-x-2">
              {/* Mobile theme toggle button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isScrolled
                    ? theme === 'dark'
                      ? 'bg-gray-800 text-gray-200'
                      : 'bg-gray-100 text-gray-700'
                    : 'bg-white/10 text-white'
                }`}
                aria-label={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
              >
                {theme === 'dark' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </motion.button>
              
              <motion.button 
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg ${isScrolled ? (theme === 'dark' ? 'text-gray-200' : 'text-gray-900') : 'text-white'}`}
                aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu renderizado fora da hierarquia do header usando portal */}
      {isBrowser && createPortal(
        <AnimatePresence>{MobileMenu()}</AnimatePresence>, 
        document.body
      )}
    </>
  );
};

export default Header;